<template>
    <v-container :fluid="true" class="max-height pb-0 px-0">
        <div class="live-panels h-100" ref="panelsContainer">
            <div class="live-panel h-100" :style="{ width: paneSizes[0] + '%' }">
                <device-list
                    :companyLocations="companyLocations"
                    :new-device-selection="selectedDevice"
                    :fleet-filter="fleetFilter"
                    :server-fleet-counts="serverFleetCounts"
                    @loaded="updateDevices"
                    @nearestToDevice="(device)=>deviceToNearest=device"
                    @zoomToDevice="(device)=>deviceToZoom=device"
                    @select-device="deviceSelection"
                    @clearFleetFilter="fleetFilter=null"
                />
            </div>
            <div class="panel-divider" @mousedown="startResize(0, $event)"/>
            <div class="live-panel h-100" :style="{ width: paneSizes[1] + '%' }">
                <div class="d-flex flex-column h-100 justify-space-between">
                    <devices-on-map
                        :devices="devices"
                        :headers="deviceHeaders"
                        :zoomToDevice="deviceToZoom"
                        :selected-device="selectedDevice"
                        :nearestToDevice="deviceToNearest"
                        :companyLocations="companyLocations"
                        :active-fleet-filter="fleetFilter"
                        :server-fleet-counts="serverFleetCounts"
                        @select-device="deviceSelection"
                        @openNotificationsTab="()=>{activeTab= true}"
                        @filterChanged="(key)=>fleetFilter=key"
                        @devices-stale="debouncedFleetCountRefresh"
                    />
                    <device-alert-list
                        :active-tab="activeTab"
                        :asset-id="selectedAssetId"
                        @tabSwitched="(newTab)=>{activeTab = newTab}"
                    />
                </div>
            </div>
            <div class="panel-divider" @mousedown="startResize(1, $event)"/>
            <div class="live-panel h-100" :style="{ width: paneSizes[2] + '%' }">
                <div class="d-flex flex-column h-100 justify-space-between">
                    <recent-trips
                        :selected-device="selectedDevice"
                        @selected-trip="(trip)=>{selectedTrip = trip}"
                    />
                    <trip-route-map
                        :selected-device="selectedDevice"
                        :selected-trip="selectedTrip"
                    />
                </div>
            </div>
        </div>
    </v-container>
</template>

<script setup>
import {onBeforeUnmount, onMounted, provide, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import pahoMqttClient from '@/plugins/mqttClient';
import {fetchMqttToken, msUntilRefresh, companyTopicPrefix} from "@/composables/mqttAuth";
import {decryptMqttPayload, clearKeyCache} from "@/composables/mqttCrypto";
import {primaryDaEvent, daEventLabels} from "@/composables/daEvents";
import DeviceList from "@/components/cards/DeviceList.vue";
import RecentTrips from "@/components/cards/RecentTrips.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import DevicesOnMap from "@/components/cards/DevicesOnMap.vue";
import DeviceAlertList from "@/components/cards/DeviceAlertList.vue";
import {useOnboardingStore} from "@/stores/onboardingStore";

const PANE_STORAGE_KEY = 'livePaneSizes';
const MIN_PANE_PCT = 15;
const defaultPaneSizes = [25, 50, 25];
const savedSizes = JSON.parse(localStorage.getItem(PANE_STORAGE_KEY) || 'null');
const paneSizes = ref(savedSizes && savedSizes.length === 3 ? savedSizes : [...defaultPaneSizes]);
const panelsContainer = ref(null);

let resizingIndex = null;
const startResize = (dividerIndex, event) => {
    event.preventDefault();
    resizingIndex = dividerIndex;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
};
const onMouseMove = (event) => {
    if (resizingIndex === null || !panelsContainer.value) return;
    const container = panelsContainer.value;
    const rect = container.getBoundingClientRect();
    const totalWidth = rect.width;
    const mouseX = event.clientX - rect.left;
    const mousePct = (mouseX / totalWidth) * 100;

    const sizes = [...paneSizes.value];
    const leftIndex = resizingIndex;
    const rightIndex = resizingIndex + 1;
    const leftStart = sizes.slice(0, leftIndex).reduce((a, b) => a + b, 0);
    const combinedSize = sizes[leftIndex] + sizes[rightIndex];

    let newLeft = mousePct - leftStart;
    let newRight = combinedSize - newLeft;

    if (newLeft < MIN_PANE_PCT) {
        newLeft = MIN_PANE_PCT;
        newRight = combinedSize - newLeft;
    }
    if (newRight < MIN_PANE_PCT) {
        newRight = MIN_PANE_PCT;
        newLeft = combinedSize - newRight;
    }

    sizes[leftIndex] = newLeft;
    sizes[rightIndex] = newRight;
    paneSizes.value = sizes;
};
const onMouseUp = () => {
    resizingIndex = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    localStorage.setItem(PANE_STORAGE_KEY, JSON.stringify(paneSizes.value));
    window.dispatchEvent(new Event('resize'));
};

const devices = ref([])
// ?fleet_status=non_reporting from the menu count opens on that chip.
const liveRoute = useRoute()
const fleetFilter = ref(liveRoute.query.fleet_status || null)
watch(() => liveRoute.query.fleet_status, (status) => {
    if (liveRoute.name === 'live-view') fleetFilter.value = status || null
})
const serverFleetCounts = ref([])
const {user} = useAuthStore()
const {showSnackbar} = useSnackbarStore()

// Suppress repeated DA-event snackbars for the same device for a short
// window: rev 6 firmware can fire one event per 50 Hz emit cycle and
// motion-progress packets recur every few seconds, so unrelated bursts
// can otherwise stack a stream of identical toasts.
const _daEventLastShownAt = new Map() // slno -> Date.now()
const DA_EVENT_TOAST_THROTTLE_MS = 4000

const surfaceDaEvent = (slno, payload) => {
    const flags = Number(payload?.da_event_flags) || 0
    if (!flags) return
    const primary = primaryDaEvent(flags)
    if (!primary) return
    const now = Date.now()
    const last = _daEventLastShownAt.get(slno) || 0
    if (now - last < DA_EVENT_TOAST_THROTTLE_MS) return
    _daEventLastShownAt.set(slno, now)
    const device = devices.value.find(d => String(d.slno) === String(slno))
    const who = device?.asset_label || device?.vin || device?.acquired_vin || slno
    showSnackbar(`${who}: ${daEventLabels(flags)}`, primary.color, 4500)
}

// --- Debounced server fleet count refresh ---
let fleetCountDebounceTimer = null;
const FLEET_COUNT_DEBOUNCE_MS = 15000;

const fetchFleetCounts = () => {
    send("get", "assets/fleet-counts")
        .then((response) => {
            serverFleetCounts.value = response.data.data || [];
        })
        .catch((err) => console.error("Failed to fetch fleet counts:", err));
};

const debouncedFleetCountRefresh = () => {
    if (fleetCountDebounceTimer) clearTimeout(fleetCountDebounceTimer);
    fleetCountDebounceTimer = setTimeout(fetchFleetCounts, FLEET_COUNT_DEBOUNCE_MS);
};
const activeTab = ref(true)
const deviceHeaders = ref([])
const deviceToZoom = ref(null)
const selectedTrip = ref(null)
const selectedDevice = ref(null)
const deviceToNearest = ref(null)
const selectedAssetId = ref(null)
const activeSubscriptions = new Set()
const mqttTopicMap = new Map() // topic -> slno mapping
const payloadKeyMap = new Map() // topic -> hex key for AES-256-GCM decryption
const latestLocations = ref(new Map())
const lastMqttUpdate = ref(null)
const companyLocations = ref([])
provide('lastMqttUpdate', lastMqttUpdate)

// Company reload debounce
let companyReloadTimer = null;
const COMPANY_RELOAD_DEBOUNCE_MS = 5000;
const companyReloadTopic = ref(user?.company?.id ? `c/${user.company.id}/reload` : null);
const topicPrefix = companyTopicPrefix(user?.company?.id);
let mqttRefreshTimer = null;

const debouncedCompanyReload = () => {
    if (companyReloadTimer) clearTimeout(companyReloadTimer);
    companyReloadTimer = setTimeout(() => {
        console.log('[MQTT] Executing debounced company data reload');
        // Refresh device list via DeviceList component's existing fetch
        window.dispatchEvent(new CustomEvent('company-data-reload'));
        // Also refresh fleet counts
        fetchFleetCounts();
    }, COMPANY_RELOAD_DEBOUNCE_MS);
};

const updateDevices = (data) => {
    devices.value = data.devices
    deviceHeaders.value = data.headers
}

const deviceSelection = (device) => {
    selectedTrip.value = null;
    selectedDevice.value = device ?? null
    selectedAssetId.value = device?.asset_id ?? null
}

const fetchCompanyLocations = async () => {
    await send("get", `companies/locations/${user.company.id}`, {list: true})
        .then((response) => {
            companyLocations.value = response.data.data;
        }).catch((errorResponse) => console.log(errorResponse));
}

// Without an MQTT token (a 403 for the Fleet Driver role, or the broker
// signing key missing) nothing pushes positions, so the map would sit on the
// first load. Poll instead, through the same device-list reload the company
// MQTT topic triggers. Paused while the tab is hidden; stopped once MQTT
// connects.
const LIVE_POLL_MS = 30000;
let livePollTimer = null;
// Set on unmount. fetchMqttToken is awaited, so its result can land after the
// page is gone; without this the catch path would start a poll nothing stops.
let unmounted = false;

const startLivePolling = () => {
    if (livePollTimer) return;
    livePollTimer = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        window.dispatchEvent(new CustomEvent('company-data-reload'));
        fetchFleetCounts();
    }, LIVE_POLL_MS);
};

const stopLivePolling = () => {
    if (livePollTimer) clearInterval(livePollTimer);
    livePollTimer = null;
};

const scheduleMqttRefresh = () => {
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    // Reconnect with a fresh token shortly before the current one expires.
    mqttRefreshTimer = setTimeout(connectMqtt, msUntilRefresh() || 1500000);
};

const connectMqtt = async () => {
    // Clear stale subscription tracking so synchronizeSubscriptions re-subscribes all topics
    activeSubscriptions.clear();
    // Drop any existing connection first (refresh + tab-visibility reconnects).
    try {
        if (pahoMqttClient.client && pahoMqttClient.client.isConnected()) {
            pahoMqttClient.disconnect();
        }
    } catch (e) { /* ignore */ }

    let auth;
    try {
        auth = await fetchMqttToken();
    } catch (err) {
        if (unmounted) return;
        console.error('MQTT: could not obtain access token, polling instead', err);
        startLivePolling();
        return;
    }
    if (unmounted) return;
    stopLivePolling();

    const clientId = `client-${Math.random().toString(16).slice(2)}`;
    pahoMqttClient.connect(auth.wsUrl || process.env.VUE_APP_MQTT_URL, clientId, {
        // The JWT is presented as the MQTT username; the password is unused but
        // some brokers require a non-empty value.
        'username': auth.token,
        'password': 'jwt',
        onSuccess: () => {
            // The connect can complete after the page has gone.
            if (unmounted) return;
            synchronizeSubscriptions();
            pahoMqttClient.subscribe('s/reload');
            if (companyReloadTopic.value) {
                pahoMqttClient.subscribe(companyReloadTopic.value);
            }
            scheduleMqttRefresh();
        }
    });
    setupMqttMessageHandler();
};

const setupMqttMessageHandler = () => {
    pahoMqttClient.client.onMessageArrived = async (message) => {
        try {
            const topic = message.destinationName;

            // Handle company-level reload signal
            if (topic.startsWith('c/') && topic.endsWith('/reload')) {
                console.log('[MQTT] Company reload signal received');
                debouncedCompanyReload();
                return;
            }

            // Handle system reload broadcast (always plain JSON)
            if (topic === 's/reload') {
                const payload = JSON.parse(message.payloadString);
                if (payload.action === 'reload_devices') {
                    console.log('MQTT: Received s/reload - refreshing devices');
                    clearKeyCache();
                    payloadKeyMap.clear();
                    window.dispatchEvent(new CustomEvent('mqtt-token-refresh'));
                }
                return;
            }

            // Decrypt or parse device payload
            let payload;
            const hexKey = payloadKeyMap.get(topic);
            if (hexKey) {
                try {
                    const decrypted = await decryptMqttPayload(message.payloadBytes, hexKey);
                    payload = JSON.parse(decrypted);
                } catch {
                    try {
                        payload = JSON.parse(message.payloadString);
                    } catch {
                        console.warn('MQTT: Failed to decrypt or parse message on topic:', topic);
                        return;
                    }
                }
            } else {
                payload = JSON.parse(message.payloadString);
            }

            const slno = payload.slno || mqttTopicMap.get(topic) || topic.split('/').pop();

            // Handle OBD update notifications
            if (payload.type === 'obd_update') {
                const deviceIndex = devices.value.findIndex(d => String(d.slno) === String(slno));
                if (deviceIndex > -1) {
                    const device = devices.value[deviceIndex];
                    if (payload.obd_type === 'vin') {
                        device.acquired_vin = payload.acquired_vin;
                    } else if (payload.obd_type === 'mil') {
                        device.indication_lamp_status = payload.indication_lamp_status ? 1 : 0;
                    }
                }
                debouncedFleetCountRefresh();
                return;
            }

            // Guard against null/undefined/malformed timestamps that produce Invalid Date
            const newTimestamp = payload.last_message_received ? new Date(payload.last_message_received) : null;
            const prevMessage = latestLocations.value.get(slno);

            let updated = false;
            if (prevMessage) {
                const lastTimestamp = prevMessage.last_message_received ? new Date(prevMessage.last_message_received) : null;
                // Accept update if: new timestamp is valid and newer, OR previous was invalid/missing
                if (!lastTimestamp || isNaN(lastTimestamp) || (newTimestamp && !isNaN(newTimestamp) && newTimestamp > lastTimestamp)) {
                    latestLocations.value.set(slno, payload);
                    updated = true;
                }
            } else {
                latestLocations.value.set(slno, payload);
                updated = true;
            }

            if (updated) {
                lastMqttUpdate.value = { slno, data: {...payload} };
                debouncedFleetCountRefresh();
                surfaceDaEvent(slno, payload);
            }

        } catch (err) {
            console.error("Error handling incoming MQTT message:", err, message);
        }
    };
};

// Reconnect MQTT when tab becomes visible again
const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && process.env.VUE_APP_MQTT_URL) {
        if (!pahoMqttClient.client || !pahoMqttClient.client.isConnected()) {
            console.log('MQTT: Tab visible - reconnecting');
            connectMqtt();
        }
    }
};

onMounted(() => {
    if (process.env.VUE_APP_MQTT_URL) {
        connectMqtt();
    }

    if (user && user.type === 'company') {
        fetchCompanyLocations()
    }

    fetchFleetCounts();
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Track onboarding milestone
    useOnboardingStore().markLiveMapViewed();
});

onBeforeUnmount(() => {
    unmounted = true;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (fleetCountDebounceTimer) clearTimeout(fleetCountDebounceTimer);
    if (companyReloadTimer) clearTimeout(companyReloadTimer);
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    stopLivePolling();
    if (process.env.VUE_APP_MQTT_URL) {
        pahoMqttClient.unsubscribe('s/reload');
        if (companyReloadTopic.value) {
            pahoMqttClient.unsubscribe(companyReloadTopic.value);
        }
        activeSubscriptions.forEach((topic) => {
            pahoMqttClient.unsubscribe(topic);
        });
        activeSubscriptions.clear();
        mqttTopicMap.clear();
        payloadKeyMap.clear();
        clearKeyCache();
        pahoMqttClient.disconnect();
    }
});

// Watch for changes in the devices array
watch(devices, synchronizeSubscriptions, {deep: true});

function synchronizeSubscriptions() {
    if (!pahoMqttClient.client || !pahoMqttClient.client.isConnected()) {
        console.warn('MQTT client is not connected');
        return;
    }

    // Build topics from mqtt_topic (token-based) or fallback to slno
    const currentTopics = new Set();
    const currentSlnos = new Set();
    mqttTopicMap.clear();
    payloadKeyMap.clear();
    devices.value.forEach((device) => {
        const topic = topicPrefix + (device.mqtt_topic || `d/${device.slno}`);
        currentTopics.add(topic);
        currentSlnos.add(String(device.slno));
        mqttTopicMap.set(topic, device.slno);
        if (device.mqtt_payload_key) {
            payloadKeyMap.set(topic, device.mqtt_payload_key);
        }
    });

    // Clean up latestLocations for devices no longer in the list
    for (const slno of latestLocations.value.keys()) {
        if (!currentSlnos.has(String(slno))) {
            latestLocations.value.delete(slno);
        }
    }

    // Subscribe to topics for new devices
    currentTopics.forEach((topic) => {
        if (!activeSubscriptions.has(topic)) {
            pahoMqttClient.subscribe(topic);
            activeSubscriptions.add(topic);
            console.log(`Subscribed to: ${topic}`);
        }
    });

    // Unsubscribe from topics that are no longer in the devices array
    activeSubscriptions.forEach((topic) => {
        if (!currentTopics.has(topic)) {
            pahoMqttClient.unsubscribe(topic);
            activeSubscriptions.delete(topic);
            console.log(`Unsubscribed from: ${topic}`);
        }
    });
}
</script>

<style scoped>
.live-panels {
    display: flex;
    overflow: hidden;
}

.live-panel {
    overflow: hidden;
    padding: 0 4px;
}

.panel-divider {
    width: 4px;
    min-width: 4px;
    cursor: col-resize;
    background: transparent;
    position: relative;
    z-index: 1;
    transition: background 0.2s;
}

.panel-divider:hover,
.panel-divider:active {
    background: rgba(var(--v-theme-primary), 0.3);
}

.panel-divider::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 3px;
    height: 30px;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.2);
}

.panel-divider:hover::after {
    background: rgba(var(--v-theme-primary), 0.6);
    height: 40px;
}
</style>
